import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  getSharedTrip,
  getSharedTripDays,
  getSharedTripActivities,
} from '@/services/sharedTripService'

const CATEGORY_COLOR: Record<string, string> = {
  transport: '#c97f4a',
  accommodation: '#6e9aa8',
  discovery: '#c9a15a',
  meal: '#9a8fb0',
}

function formatDay(date: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(`${date}T00:00:00Z`))
}

export function SharedTripPage() {
  const { t } = useTranslation()
  const { token } = useParams<{ token: string }>()

  const tripQuery = useQuery({
    queryKey: ['shared-trip', token],
    queryFn: () => getSharedTrip(token!),
    enabled: !!token,
  })

  const daysQuery = useQuery({
    queryKey: ['shared-trip-days', token],
    queryFn: () => getSharedTripDays(token!),
    enabled: !!token && !!tripQuery.data,
  })

  const activitiesQuery = useQuery({
    queryKey: ['shared-trip-activities', token],
    queryFn: () => getSharedTripActivities(token!),
    enabled: !!token && !!tripQuery.data,
  })

  if (tripQuery.isLoading) {
    return <p className="bg-night p-10 text-mist">{t('sharedTrip.loading')}</p>
  }

  const trip = tripQuery.data
  if (!trip) {
    return <p className="bg-night p-10 text-mist">{t('sharedTrip.notFound')}</p>
  }

  const days = daysQuery.data ?? []
  const activities = activitiesQuery.data ?? []

  return (
    <div className="min-h-screen bg-night px-10 py-7">
      <div className="mb-3.5 font-mono text-xs tracking-[0.14em] text-brass-dim uppercase">
        {t('sharedTrip.readOnly')}
      </div>
      <h1 className="font-display text-3xl text-ivory">{trip.title}</h1>
      <div className="mt-1.5 font-mono text-xs text-brass-dim">
        {trip.destination} — {trip.start_date} → {trip.end_date}
      </div>

      <div className="mt-8">
        {days.map((day) => {
          const dayActivities = activities.filter((a) => a.day_id === day.id)
          return (
            <div key={day.id} className="mb-8">
              <h2 className="mb-3 font-display text-lg text-ivory">{formatDay(day.date)}</h2>
              {dayActivities.length === 0 ? (
                <p className="text-sm text-mist">{t('sharedTrip.noActivities')}</p>
              ) : (
                dayActivities.map((activity) => (
                  <div key={activity.id} className="mb-3.5 grid grid-cols-[44px_1fr] gap-4">
                    <div className="pt-4 text-right font-mono text-xs text-brass-dim">
                      {activity.start_time?.slice(0, 5)}
                    </div>
                    <div className="rounded-md border border-line bg-panel px-4 py-3.5">
                      <div className="flex items-center justify-between gap-2.5">
                        <span className="flex items-center gap-2 font-sans text-[15px] font-medium text-ivory">
                          <span
                            className="inline-block h-2.5 w-2.5 rounded-full"
                            style={{ background: CATEGORY_COLOR[activity.category ?? ''] }}
                          />
                          {activity.title}
                        </span>
                        {activity.category && (
                          <span
                            className="font-mono text-[10px] tracking-wide uppercase"
                            style={{ color: CATEGORY_COLOR[activity.category] }}
                          >
                            {t(`itinerary.category.${activity.category}`)}
                          </span>
                        )}
                      </div>
                      {activity.location && (
                        <div className="mt-1 text-sm text-mist">{activity.location}</div>
                      )}
                      {activity.note && (
                        <div className="mt-1 text-sm text-mist">{activity.note}</div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
